import React from 'react';
import { motion, useInView } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { getThemeColors } from '../utils/themeColors';
import ThemeToggle from '../components/ThemeToggle';
import SocialProof from '../components/SocialProof';
import ExitIntentCTA from '../components/ExitIntentCTA';
import Logo7Ases from '../components/Logo7Ases';
import { useAlexNudge } from '../hooks/useAlexNudge';

// Animation variants for horizontal scroll effects
const slideFromLeft = {
  hidden: { opacity: 0, x: -100 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

const slideFromRight = {
  hidden: { opacity: 0, x: 100 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};

const cardVariant = {
  hidden: { opacity: 0, x: 80, scale: 0.9 },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

const benefitVariant = {
  hidden: { opacity: 0, x: -60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, ease: 'easeOut' }
  }
};

// Animated section wrapper
function AnimatedSection({ children, variants = fadeUp, className = '', ...props }: any) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={variants}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

export default function LandingSeguroAuto() {
  // Widget do meu-agente (canal web_widget): no-op ate window.Alex existir.
  useAlexNudge('seguros');
  const { isDark, isHighContrast } = useTheme();
  const colors = getThemeColors('dark', isDark, isHighContrast);

  return (
    <div style={{ 
      fontFamily: 'Inter, sans-serif', 
      background: colors.primaryBg, 
      minHeight: '100vh', 
      color: colors.primaryText,
      transition: 'all 0.3s ease'
    }}>
      <ThemeToggle />
      
      {/* Header */}
      <header style={{ 
        background: colors.headerBg, 
        backdropFilter: 'blur(10px)', 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        zIndex: 1000, 
        boxShadow: `0 0px 0px ${colors.shadowColor}`,
        borderBottom: `1px solid ${colors.borderColor}`,
        height: 'clamp(80px, 12vw, 100px)'
      }}>
        <div style={{ 
          maxWidth: 1200, 
          margin: '0 auto', 
          padding: '1rem 20px', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center'
        }}>
          {/* Logo SVG local com animacao */}
          <motion.div
            style={{
              flex: '0 0 auto',
              minWidth: 0,
              overflow: 'hidden'
            }}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <Logo7Ases height="clamp(45px, 10vw, 65px)" />
          </motion.div>
        </div>
      </header>
      
      <main style={{ paddingTop: 0 }}>
        {/* Hero Section */}
        <section style={{ 
          textAlign: 'center', 
          color: 'white', 
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '0',
          background: colors.heroGradient,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
            pointerEvents: 'none'
          }} />
          <motion.div
            style={{
              position: 'relative',
              zIndex: 1,
              padding: '0 clamp(20px, 5vw, 40px)',
              maxWidth: 'clamp(600px, 80vw, 800px)',
              width: '100%'
            }}
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.h1
              variants={slideFromLeft}
              style={{
                fontSize: 'clamp(2rem, 6vw, 4rem)',
                fontWeight: 800,
                marginBottom: 'clamp(1rem, 3vw, 1.5rem)',
                lineHeight: 1.2,
                textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                letterSpacing: '-0.02em'
              }}
            >
              Seguro Automóvel 7Ases
            </motion.h1>
            <motion.p
              variants={slideFromRight}
              style={{
                fontSize: 'clamp(1rem, 3vw, 1.5rem)',
                marginBottom: 'clamp(1.5rem, 4vw, 2rem)',
                opacity: 0.95,
                maxWidth: 600,
                marginLeft: 'auto',
                marginRight: 'auto',
                textShadow: '0 1px 5px rgba(0,0,0,0.2)',
                lineHeight: 1.5
              }}
            >
              Cotação rápida, fácil e personalizada pelo WhatsApp. Atendimento 100% digital com inteligência artificial e especialistas em seguros.
            </motion.p>
            <motion.a
              variants={fadeUp}
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.98 }}
              href="https://wa.me/554791246688?text=Olá! Quero fazer uma cotação de seguro automóvel" 
               target="_blank" 
               rel="noopener noreferrer" 
               style={{ 
                 background: colors.ctaGradient, 
                 color: 'white', 
                 padding: 'clamp(14px, 3vw, 18px) clamp(30px, 6vw, 40px)', 
                 borderRadius: 50, 
                 textDecoration: 'none', 
                 fontWeight: 700, 
                 fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', 
                 display: 'inline-flex', 
                 alignItems: 'center', 
                 gap: 'clamp(8px, 2vw, 12px)', 
                 boxShadow: `0 10px 30px ${colors.shadowColor}`,
                 transition: 'all 0.3s ease',
                 transform: 'translateY(0)',
                 whiteSpace: 'nowrap'
               }}
            >
              <i className="fab fa-whatsapp"></i> Fazer Cotação Agora
            </motion.a>
          </motion.div>
        </section>
        
        {/* Features Section - Animated */}
        <AnimatedSection
          variants={slideFromRight}
          style={{
            background: colors.secondaryBg,
            padding: 'clamp(60px, 10vw, 80px) 0',
            borderTop: `1px solid ${colors.borderColor}`,
            borderBottom: `1px solid ${colors.borderColor}`,
            overflow: 'hidden'
          }}
        >
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 clamp(20px, 5vw, 40px)' }}>
            <motion.h2
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              style={{
                textAlign: 'center',
                fontSize: 'clamp(1.8rem, 5vw, 3rem)',
                fontWeight: 800,
                marginBottom: 'clamp(2rem, 5vw, 3rem)',
                color: colors.primaryText
              }}
            >
              Por que escolher a 7Ases?
            </motion.h2>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
              variants={staggerContainer}
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(280px, 40vw, 300px), 1fr))',
                gap: 'clamp(1.5rem, 3vw, 2rem)',
                marginTop: 'clamp(2rem, 4vw, 3rem)'
              }}
            >
              {/* Feature Cards with horizontal slide animation */}
              {[
                { icon: 'fas fa-robot', title: 'IA Inteligente', desc: 'Atendimento automatizado com inteligência artificial que entende suas necessidades e oferece as melhores opções de seguro.' },
                { icon: 'fas fa-clock', title: 'Cotação Rápida', desc: 'Receba sua cotação em minutos, não em dias. Processo 100% digital e sem complicações.' },
                { icon: 'fas fa-shield-alt', title: 'Segurança Total', desc: 'Proteção completa para seu veículo com as melhores seguradoras do mercado e cobertura personalizada.' },
                { icon: 'fas fa-dollar-sign', title: 'Melhor Preço', desc: 'Compare preços e encontre a melhor oferta para seu seguro automóvel com as melhores condições do mercado.' },
                { icon: 'fas fa-headset', title: 'Suporte 24/7', desc: 'Atendimento humanizado disponível 24 horas por dia, 7 dias por semana via WhatsApp.' },
                { icon: 'fas fa-mobile-alt', title: '100% Digital', desc: 'Todo o processo é feito pelo WhatsApp, sem necessidade de sair de casa ou ir até uma agência.' }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  variants={cardVariant}
                  whileHover={{ y: -8, scale: 1.02 }}
                  style={{
                    background: colors.cardBg,
                    padding: 'clamp(1.5rem, 4vw, 2rem)',
                    borderRadius: 20,
                    boxShadow: colors.cardShadow,
                    textAlign: 'center',
                    border: `1px solid ${colors.borderColor}`,
                    cursor: 'pointer'
                  }}
                >
                  <motion.div
                    whileHover={{ rotate: 360, scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                    style={{
                      width: 'clamp(60px, 12vw, 80px)',
                      height: 'clamp(60px, 12vw, 80px)',
                      background: colors.featureGradient,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto clamp(1rem, 2.5vw, 1.5rem)',
                      color: 'white',
                      fontSize: 'clamp(1.5rem, 3vw, 2rem)'
                    }}
                  >
                    <i className={feature.icon}></i>
                  </motion.div>
                  <h3 style={{
                    fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
                    fontWeight: 700,
                    marginBottom: 'clamp(0.8rem, 2vw, 1rem)',
                    color: colors.primaryText
                  }}>
                    {feature.title}
                  </h3>
                  <p style={{
                    color: colors.secondaryText,
                    lineHeight: 1.6,
                    fontSize: 'clamp(0.9rem, 2.5vw, 1rem)'
                  }}>
                    {feature.desc}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </AnimatedSection>
        
        {/* Benefits Section - Animated from Left */}
        <AnimatedSection
          variants={slideFromLeft}
          style={{
            background: colors.benefitsGradient,
            padding: 'clamp(60px, 10vw, 80px) 0',
            color: 'white',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 70% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
            pointerEvents: 'none'
          }} />
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 clamp(20px, 5vw, 40px)', position: 'relative', zIndex: 1 }}>
            <motion.h2
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              style={{
                textAlign: 'center',
                fontSize: 'clamp(1.8rem, 5vw, 3rem)',
                fontWeight: 800,
                marginBottom: 'clamp(2rem, 5vw, 3rem)',
                textShadow: '0 2px 10px rgba(0,0,0,0.3)'
              }}
            >
              Vantagens do Seguro 7Ases
            </motion.h2>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
              variants={staggerContainer}
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(250px, 35vw, 300px), 1fr))',
                gap: 'clamp(1.5rem, 3vw, 2rem)'
              }}
            >
              {[
                { icon: 'fas fa-dollar-sign', title: 'Economia Real', desc: 'Compare preços e encontre a melhor oferta' },
                { icon: 'fas fa-clock', title: 'Processo Simplificado', desc: 'Cotação em menos de 5 minutos' },
                { icon: 'fas fa-shield-alt', title: 'Cobertura Completa', desc: 'Proteção total para seu veículo' },
                { icon: 'fas fa-user-tie', title: 'Atendimento Personalizado', desc: 'IA + especialistas humanos' },
                { icon: 'fas fa-file-alt', title: 'Sem Burocracia', desc: 'Documentação digital simplificada' },
                { icon: 'fas fa-headset', title: 'Suporte Contínuo', desc: 'Assistência 24 horas por dia' }
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  variants={benefitVariant}
                  whileHover={{ scale: 1.03, x: 5 }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'clamp(0.8rem, 2vw, 1rem)',
                    padding: 'clamp(1.2rem, 3vw, 1.5rem)',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: 15,
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    cursor: 'pointer'
                  }}
                >
                  <motion.div
                    whileHover={{ scale: 1.2, rotate: 10 }}
                    style={{
                      width: 'clamp(40px, 8vw, 50px)',
                      height: 'clamp(40px, 8vw, 50px)',
                      background: 'rgba(255,255,255,0.2)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)',
                      flexShrink: 0
                    }}
                  >
                    <i className={benefit.icon}></i>
                  </motion.div>
                  <div style={{ minWidth: 0 }}>
                    <h4 style={{
                      margin: 0,
                      marginBottom: 'clamp(0.3rem, 1vw, 0.5rem)',
                      fontWeight: 600,
                      fontSize: 'clamp(1rem, 2.5vw, 1.1rem)'
                    }}>
                      {benefit.title}
                    </h4>
                    <p style={{
                      margin: 0,
                      opacity: 0.9,
                      fontSize: 'clamp(0.8rem, 2.2vw, 0.9rem)',
                      lineHeight: 1.4
                    }}>
                      {benefit.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </AnimatedSection>

        {/* Social Proof Section */}
        <SocialProof />
        
        {/* CTA Section - Animated Scale In */}
        <AnimatedSection
          variants={{
            hidden: { opacity: 0, scale: 0.9 },
            visible: {
              opacity: 1,
              scale: 1,
              transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }
            }
          }}
          style={{
            background: colors.secondaryBg,
            padding: 'clamp(60px, 10vw, 80px) 0',
            textAlign: 'center',
            borderTop: `1px solid ${colors.borderColor}`
          }}
        >
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            style={{
              maxWidth: 600,
              margin: '0 auto',
              padding: '0 clamp(20px, 5vw, 40px)'
            }}
          >
            <motion.h2
              variants={fadeUp}
              style={{
                fontSize: 'clamp(1.8rem, 5vw, 3rem)',
                fontWeight: 800,
                marginBottom: 'clamp(1rem, 3vw, 1.5rem)',
                color: colors.primaryText
              }}
            >
              Pronto para economizar no seu seguro?
            </motion.h2>
            <motion.p
              variants={fadeUp}
              style={{
                fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                color: colors.secondaryText,
                marginBottom: 'clamp(1.5rem, 4vw, 2rem)',
                lineHeight: 1.5
              }}
            >
              Junte-se a milhares de clientes que já economizaram com a 7Ases. Faça sua cotação agora e descubra quanto você pode economizar!
            </motion.p>
            <motion.a
              variants={fadeUp}
              whileHover={{ scale: 1.08, y: -5 }}
              whileTap={{ scale: 0.95 }}
              href="https://wa.me/554791246688?text=Olá! Quero fazer uma cotação de seguro automóvel"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: colors.featureGradient,
                color: 'white',
                padding: 'clamp(16px, 3vw, 20px) clamp(30px, 6vw, 40px)',
                borderRadius: 50,
                textDecoration: 'none',
                fontWeight: 700,
                fontSize: 'clamp(1.1rem, 2.5vw, 1.3rem)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'clamp(8px, 2vw, 12px)',
                boxShadow: `0 10px 30px ${colors.shadowColor}`,
                whiteSpace: 'nowrap'
              }}
            >
              <i className="fab fa-whatsapp"></i> Fazer Cotação Gratuita
            </motion.a>
          </motion.div>
        </AnimatedSection>
      </main>
      
      {/* Footer */}
      <footer style={{ 
        background: isDark ? '#0f172a' : '#1f2937', 
        color: 'white', 
        padding: 'clamp(30px, 6vw, 40px) 0', 
        textAlign: 'center',
        borderTop: `1px solid ${colors.borderColor}`
      }}>
        <div style={{ 
          maxWidth: 1200, 
          margin: '0 auto', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          flexWrap: 'wrap', 
          gap: 'clamp(1rem, 3vw, 2rem)',
          padding: '0 clamp(20px, 5vw, 40px)'
        }}>
          <Logo7Ases height="clamp(40px, 8vw, 55px)" />
          <div style={{ 
            display: 'flex', 
            gap: 'clamp(1rem, 3vw, 2rem)', 
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            <a href="#privacidade" style={{ 
              color: '#9ca3af', 
              textDecoration: 'none', 
              transition: 'color 0.3s ease',
              fontSize: 'clamp(0.8rem, 2.2vw, 0.9rem)'
            }}>
              Política de Privacidade
            </a>
            <a href="#termos" style={{ 
              color: '#9ca3af', 
              textDecoration: 'none', 
              transition: 'color 0.3s ease',
              fontSize: 'clamp(0.8rem, 2.2vw, 0.9rem)'
            }}>
              Termos de Uso
            </a>
            <a href="#contato" style={{ 
              color: '#9ca3af', 
              textDecoration: 'none', 
              transition: 'color 0.3s ease',
              fontSize: 'clamp(0.8rem, 2.2vw, 0.9rem)'
            }}>
              Contato
            </a>
          </div>
          <p style={{ 
            margin: 0, 
            color: '#9ca3af',
            fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
            textAlign: 'center',
            width: '100%',
            marginTop: 'clamp(0.5rem, 2vw, 1rem)'
          }}>
            &copy; {new Date().getFullYear()} 7Ases Corretora de Seguros. Todos os direitos reservados.
          </p>
        </div>
      </footer>
      
      {/* WhatsApp Float Button - Animated with pulse */}
      <motion.a
        href="https://wa.me/554791246688?text=Olá! Quero fazer uma cotação de seguro automóvel"
        target="_blank"
        rel="noopener noreferrer"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, duration: 0.5, type: 'spring', stiffness: 200 }}
        whileHover={{ scale: 1.15 }}
        whileTap={{ scale: 0.9 }}
        style={{
          position: 'fixed',
          bottom: 'clamp(20px, 4vw, 30px)',
          right: 'clamp(20px, 4vw, 30px)',
          background: colors.whatsappButton,
          color: 'white',
          width: 'clamp(60px, 12vw, 75px)',
          height: 'clamp(60px, 12vw, 75px)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textDecoration: 'none',
          fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
          boxShadow: `0 4px 20px ${colors.shadowColor}`,
          zIndex: 1000
        }}
      >
        <motion.svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="currentColor"
          animate={{
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
        </motion.svg>
      </motion.a>
      
      {/* Exit Intent CTA */}
      <ExitIntentCTA />
    </div>
  );
} 